import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useMatch } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AppShell, locationIdFromPath, useDarkMode } from '@pazportal/ui';
import { useAuth } from '../contexts/AuthContext';
import { locationNavSections, navSections, PORTAL_COLOR, PORTAL_NAME, PORTAL_SUBTITLE } from '../config/navigation';
import { AdminLocationSwitcher } from './AdminLocationSwitcher';
import { useAllLocations } from '../hooks/useAllLocations';
import { usePlatformMetrics } from '../hooks/usePlatformMetrics';
import { isExternalHref, openInNewTab } from '../lib/portalUrl';
import { supabase } from '../lib/supabase';
import { useShellBrand } from '../hooks/useShellBrand';

function initialsFromName(name: string): string {
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .substring(0, 2)
    .toUpperCase();
}

function roleLabel(role: string | null): string {
  if (!role) return 'No role';
  return role.replace(/_/g, ' ');
}

export function AppLayout() {
  const { user, role, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const customerMatch = useMatch('/customers/:id');
  const customerId = customerMatch?.params.id;
  const { isDark, toggle } = useDarkMode();
  const { data: metrics } = usePlatformMetrics();
  const { data: customer } = useQuery({
    queryKey: ['admin-shell-customer', customerId],
    enabled: Boolean(customerId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('customers')
        .select('id, name')
        .eq('id', customerId!)
        .single();
      if (error) throw error;
      return data;
    },
    staleTime: 60 * 1000,
  });

  const initials = user?.email
    ? user.email.substring(0, 2).toUpperCase()
    : '??';

  const customerCount = metrics?.totalCustomers ?? 0;
  // Header badge: open Auvik alerts (when connected) + open tickets past SLA.
  const alertCount = (metrics?.auvik.openAlerts ?? 0) + (metrics?.slaBreaches ?? 0);
  const siteId = locationIdFromPath(location.pathname);
  const { data: allLocations } = useAllLocations();
  const site = siteId ? allLocations?.find((row) => row.id === siteId) : undefined;
  const onCustomer360 = Boolean(customerId && customer?.name) && !siteId;
  const activeNav = siteId ? locationNavSections(siteId) : navSections;
  const city = site?.address?.city;
  const state = site?.address?.state;

  // Hotel-brand theme for the property / customer in view (light mode only —
  // in dark mode the sidebar keeps the PazNetworks ink and the brand header is hidden).
  const brandTheme = useShellBrand(location.pathname, customer?.name);
  useEffect(() => {
    const root = document.documentElement;
    if (brandTheme) root.setAttribute('data-brand', brandTheme.key);
    else root.removeAttribute('data-brand');
    return () => root.removeAttribute('data-brand');
  }, [brandTheme]);
  const sidebarBrand = brandTheme && !isDark ? brandTheme : null;

  return (
    <AppShell
      navSections={activeNav}
      portalColor={sidebarBrand?.theme.sidebarBackground ?? PORTAL_COLOR}
      portalName={PORTAL_NAME}
      portalSubtitle={PORTAL_SUBTITLE}
      propertyName={site ? site.name : onCustomer360 ? customer!.name : 'PazNetworks, Inc.'}
      propertySubtitle={
        site
          ? [city, state].filter(Boolean).join(', ') || site.customer?.name || 'Site'
          : onCustomer360
            ? 'Customer 360'
            : metrics
              ? `${customerCount} customer${customerCount === 1 ? '' : 's'}`
              : 'Admin Workspace'
      }
      propertyInitials={
        site ? initialsFromName(site.name) : onCustomer360 ? initialsFromName(customer!.name) : 'PN'
      }
      alertCount={alertCount}
      userName={user?.email ?? 'User'}
      userRole={roleLabel(role)}
      userInitials={initials}
      brandHeader={
        sidebarBrand
          ? {
              logoSrc: sidebarBrand.theme.sidebarLogo,
              logoAlt: sidebarBrand.key.charAt(0).toUpperCase() + sidebarBrand.key.slice(1),
              title: sidebarBrand.title,
            }
          : undefined
      }
      currentPath={location.pathname}
      onNavigate={(href: string) => {
        if (isExternalHref(href)) openInNewTab(href);
        else navigate(href);
      }}
      onSignOut={signOut}
      onDarkModeToggle={toggle}
      isDarkMode={isDark}
    >
      {siteId && site?.customer_id && (
        <AdminLocationSwitcher locationId={siteId} customerId={site.customer_id} />
      )}
      <Outlet />
    </AppShell>
  );
}
